/* global React, Icon, Card, Button, Modal, Input, Segmented, toast, fmt, fmt0 */
// ============================================================
// PLUMME 3 — Privat økonomi · Forbruk
// Kategorier, beløp og endring kommer fra importerte transaksjoner
// (readmodel fra getPersonalFinanceOverview). Budsjettgrensene
// settes av brukeren og lagres via privatøkonomi-planleggingen.
// ============================================================
const { useState: pofUseState, useMemo: pofUseMemo } = React;

const POF_TINTS = ['var(--plum-700)', 'var(--plum-600)', 'var(--plum-500)', 'var(--plum-400)', 'var(--plum-300)', 'var(--success)', 'var(--warning)', 'var(--muted-2)'];
const pofColor = (index) => POF_TINTS[index % POF_TINTS.length];
function pofBudgetTone(pct) {
  if (pct > 100) return { color: 'var(--error)', icon: 'alertCircle', label: t('pkn.for.tone.over') };
  if (pct >= 90) return { color: 'var(--warning)', icon: 'trendUp', label: t('pkn.for.tone.near') };
  return { color: 'var(--success)', icon: 'check', label: t('pkn.for.tone.within') };
}

// ---------------- Periodevelger ----------------
// Verdien er månedsnummer 1-12, eller window.PoPeriods.YEAR for hele året.
const pofPeriodLabel = (period) => (window.PoPeriods.isYearPeriod(period) ? t('po.yearly') : t(`po.month.${Number(period)}`));

function PoPeriodPicker({ availableMonths = [], onClose, onSelect, value, year }) {
  const months = new Set(availableMonths);
  const pick = (next) => { onSelect(next); onClose(); };
  return <Modal open onClose={onClose} max={420} title={`${t('po.selectPeriod')} ${year}`}>
    <div className="po-period-grid">
      {Array.from({ length: 12 }, (_, index) => index + 1).map((month) => <button key={month} type="button"
        className={`po-period-opt${value === month ? ' on' : ''}`} disabled={!months.has(month)}
        title={months.has(month) ? '' : t('pkn.for.noImportedMonth')}
        onClick={() => pick(month)}>{t(`po.month.${month}`)}</button>)}
    </div>
    <button type="button" className={`po-period-opt po-period-year${window.PoPeriods.isYearPeriod(value) ? ' on' : ''}`}
      onClick={() => pick(window.PoPeriods.YEAR)}>{t('po.yearOverview')}</button>
  </Modal>;
}

// ---------------- Kakediagram ----------------
function PoDonut({ data, onSelect, period, selected, total }) {
  const radius = 88;
  const circumference = 2 * Math.PI * radius;
  let accumulated = 0;
  const active = selected !== null && selected !== undefined ? data[selected] : null;
  return <div className="po-donut-wrap">
    <svg width="240" height="240" viewBox="0 0 240 240">
      <circle cx="120" cy="120" r={radius} fill="none" stroke="var(--panel-2)" strokeWidth="30" />
      {data.map((entry, index) => {
        const fraction = total ? entry.amount / total : 0;
        const segment = <circle data-app-control="true" key={entry.name} cx="120" cy="120" r={radius} fill="none" stroke={entry.color}
          strokeWidth={selected === index ? 38 : 30} strokeDasharray={`${circumference * fraction} ${circumference}`} strokeDashoffset={-circumference * accumulated}
          transform="rotate(-90 120 120)" style={{ cursor: 'pointer', opacity: selected !== null && selected !== undefined && selected !== index ? 0.4 : 1, transition: 'stroke-width .15s' }}
          onClick={() => onSelect(selected === index ? null : index)} />;
        accumulated += fraction;
        return segment;
      })}
      <text x="120" y="112" textAnchor="middle" fontSize="13" fontWeight="700" fill="var(--muted)">{active ? active.name : pofPeriodLabel(period)}</text>
      <text x="120" y="140" textAnchor="middle" fontSize="22" fontWeight="900" fill={active ? active.color : 'var(--ink)'}>{fmt0(active ? active.amount : total)} kr</text>
    </svg>
  </div>;
}

// ---------------- Budsjett ----------------
function PoBudgetEditor({ base, custom, onClose, onSave }) {
  const [rows, setRows] = pofUseState(() => base.map((entry) => ({ amount: entry.amount, budget: entry.budget ? String(entry.budget) : '', color: entry.color, name: entry.name })));
  const [income, setIncome] = pofUseState(() => ({
    grossMonthly: custom?.income?.grossMonthly ? String(custom.income.grossMonthly) : '',
    netMonthly: custom?.income?.netMonthly ? String(custom.income.netMonthly) : '',
    taxPercent: custom?.income?.taxPercent ? String(custom.income.taxPercent) : '',
  }));
  const [incomeBasis, setIncomeBasis] = pofUseState(() => custom?.income?.netMonthly && !custom?.income?.grossMonthly ? 'net' : 'gross');
  const [customRows, setCustomRows] = pofUseState(() => (Array.isArray(custom?.custom) ? custom.custom : Array.isArray(custom) ? custom : []).map((entry) => ({ amount: entry.amount || 0, budget: String(entry.budget), color: entry.color, name: entry.name, showAsGroup: entry.showAsGroup === true })));
  const setRow = (index, patch) => setRows((current) => current.map((row, key) => (key === index ? { ...row, ...patch } : row)));
  const setCustomRow = (index, patch) => setCustomRows((current) => current.map((row, key) => (key === index ? { ...row, ...patch } : row)));
  const addCustom = () => setCustomRows((current) => [...current, { amount: 0, budget: '', color: POF_TINTS[(current.length + 4) % POF_TINTS.length], name: '', showAsGroup: false }]);
  const removeCustom = (index) => setCustomRows((current) => current.filter((_, key) => key !== index));
  const totalBudget = [...rows, ...customRows].reduce((sum, row) => sum + (Math.max(0, Number(row.budget)) || 0), 0);
  const validCustom = customRows.every((row) => row.name.trim() && Number(row.budget) > 0);
  const recalcIncome = (patch, nextBasis = incomeBasis) => {
    const next = { ...income, ...patch };
    const taxPercent = Math.max(0, Math.min(99.9, Number(next.taxPercent) || 0));
    const factor = Math.max(0.001, 1 - taxPercent / 100);
    if (nextBasis === 'net') {
      const net = Number(next.netMonthly) || 0;
      next.grossMonthly = net > 0 ? String(Math.round(net / factor)) : '';
    } else {
      const gross = Number(next.grossMonthly) || 0;
      next.netMonthly = gross > 0 ? String(Math.round(gross * factor)) : '';
    }
    setIncome(next);
  };
  const save = () => {
    const overrides = {};
    rows.forEach((row) => { if (row.budget !== '') overrides[row.name] = Math.max(0, Math.round(Number(row.budget)) || 0); });
    const cleaned = customRows.filter((row) => row.name.trim() && Number(row.budget) > 0)
      .map((row) => ({ amount: Math.max(0, Math.round(Number(row.amount)) || 0), budget: Math.max(0, Math.round(Number(row.budget)) || 0), color: row.color, name: row.name.trim(), showAsGroup: row.showAsGroup === true }));
    const grossMonthly = Math.max(0, Math.round(Number(income.grossMonthly)) || 0);
    const taxPercent = Math.max(0, Math.min(100, Number(income.taxPercent) || 0));
    const netMonthly = Math.max(0, Math.round(Number(income.netMonthly)) || (grossMonthly ? Math.round(grossMonthly * (1 - taxPercent / 100)) : 0));
    onSave({ custom: cleaned, income: { grossMonthly, netMonthly, taxPercent }, overrides });
  };
  return <Modal open onClose={onClose} max={560} title={t('po.adjustBudget')}
    titleRight={<span className="po-bud-ed-tot">{t('sharedText.po.budgetTotal')} <b>{fmt(totalBudget)}</b></span>}
    footer={<React.Fragment>
      <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
      <Button disabled={!validCustom} onClick={save} data-help={t('helpTopics.budgetPeriod.body')} data-help-title={t('helpTopics.budgetPeriod.title')}>{t('po.saveBudget')}</Button>
    </React.Fragment>}>
    <div className="po-bud-ed">
      <div className="po-bud-ed-lbl">{t('pkn.for.plannedIncome')}</div>
      <div className="t-xs muted" style={{ fontWeight: 700, marginTop: -6 }}>{t('pkn.for.incomeHint')}</div>
      <div className="po-bud-ed-income">
        <label><span>{t('pkn.for.grossPerMonth')}</span><div className="po-bud-ed-inp"><Input type="number" value={income.grossMonthly} onChange={(event) => { setIncomeBasis('gross'); recalcIncome({ grossMonthly: event.target.value }, 'gross'); }} placeholder="0" /><span>kr</span></div></label>
        <label><span>{t('sharedText.set.bank.typeTax')}</span><div className="po-bud-ed-inp"><Input type="number" value={income.taxPercent} onChange={(event) => recalcIncome({ taxPercent: event.target.value })} placeholder="0" /><span>%</span></div></label>
        <label><span>{t('pkn.for.netToAccount')}</span><div className="po-bud-ed-inp"><Input type="number" value={income.netMonthly} onChange={(event) => { setIncomeBasis('net'); recalcIncome({ netMonthly: event.target.value }, 'net'); }} placeholder="0" /><span>kr</span></div></label>
      </div>
      <div className="po-bud-ed-lbl">{t('po.monthlyLimitPerCategory')}</div>
      <div className="t-xs muted" style={{ fontWeight: 700, marginTop: -6 }}>{t('pkn.for.limitHint')}</div>
      {rows.length === 0 && <div className="t-sm muted" style={{ fontWeight: 600 }}>{t('po.noCategoriesImported')}</div>}
      {rows.map((row, index) => <div className="po-bud-ed-row" key={row.name}>
        <span className="aii-cat-dot" style={{ background: row.color, height: 12, width: 12 }} />
        <span className="po-bud-ed-name">{row.name}</span>
        <span className="po-bud-ed-used">{t('pkn.for.used', { amount: fmt(row.amount) })}</span>
        <div className="po-bud-ed-inp"><Input type="number" value={row.budget} onChange={(event) => setRow(index, { budget: event.target.value })} placeholder="0" /><span>kr</span></div>
      </div>)}

      {customRows.length > 0 && <div className="po-bud-ed-lbl" style={{ marginTop: 6 }}>{t('po.ownCategories')}</div>}
      {customRows.map((row, index) => <div className="po-bud-ed-row cust" key={`custom-${index}`}>
        <div className="pf-pick po-bud-ed-colors">
          {POF_TINTS.map((tint) => <button key={tint} type="button" className={`pf-pick-color${row.color === tint ? ' on' : ''}`} style={{ background: tint }} onClick={() => setCustomRow(index, { color: tint })} aria-label={t('po.pickColour')} />)}
        </div>
        <Input value={row.name} onChange={(event) => setCustomRow(index, { name: event.target.value })} placeholder={t('po.categoryName')} />
        <div className="po-bud-ed-inp"><Input type="number" value={row.budget} onChange={(event) => setCustomRow(index, { budget: event.target.value })} placeholder="0" /><span>kr</span></div>
        <button className="po-bud-ed-del" onClick={() => removeCustom(index)} aria-label={t('po.removeCategory')} title={t('po.removeCategory')} data-help={t('helpTopics.budgetPeriod.body')} data-help-title={t('helpTopics.budgetPeriod.title')}><Icon name="trash" size={16} /></button>
        <label className="po-bud-ed-group">
          <input type="checkbox" checked={row.showAsGroup === true} onChange={(event) => setCustomRow(index, { showAsGroup: event.target.checked })} />
          <span>{t('pkn.for.showAsGroup')}</span>
        </label>
      </div>)}

      <button className="po-bud-ed-add" onClick={addCustom} type="button"><Icon name="plus" size={16} />{t('po.addCategory')}</button>
    </div>
  </Modal>;
}

function PoBudgetPanel({ budgets, categories, onSaveBudgets, periodMonthCount = 1 }) {
  const Sec = window.PoSecHead;
  const [editOpen, setEditOpen] = pofUseState(false);
  const base = categories.map((entry) => ({ ...entry, amount: Number(entry.budgetAmount || 0), budget: budgets.overrides[entry.name] }));
  const spentByName = new Map(base.map((entry) => [entry.name, entry.amount]));
  // Grensene brukeren setter er månedlige. Viser vi hele året, må de ganges opp
  // med antall måneder som har data — ellers ser alt ut som kraftig overforbruk.
  const months = Math.max(1, Number(periodMonthCount) || 1);
  const withBudget = [
    ...base.filter((entry) => entry.budget > 0),
    // En egen kategori kan ogsaa ligge som hovedkategori i lista. Da skal
    // forbruket hentes derfra, og raden skal ikke telles to ganger.
    ...(budgets.custom || [])
      .filter((entry) => !base.some((row) => row.name === entry.name && row.budget > 0))
      .map((entry) => ({ ...entry, amount: spentByName.has(entry.name) ? spentByName.get(entry.name) : (entry.amount || 0) })),
  ].map((entry) => ({ ...entry, budget: Number(entry.budget || 0) * months }));
  const spent = withBudget.reduce((sum, entry) => sum + entry.amount, 0);
  const budget = withBudget.reduce((sum, entry) => sum + entry.budget, 0);
  const totalPct = budget ? Math.round(spent / budget * 100) : 0;
  const tone = pofBudgetTone(totalPct);
  const remaining = budget - spent;
  const overCount = withBudget.filter((entry) => entry.amount > entry.budget).length;
  const plannedIncome = budgets?.income || {};
  const netIncome = Number(plannedIncome.netMonthly || 0);
  const grossIncome = Number(plannedIncome.grossMonthly || 0);
  const taxPercent = Number(plannedIncome.taxPercent || 0);
  const derivedNetIncome = netIncome || Math.round(grossIncome * (1 - taxPercent / 100));
  const budgetShare = derivedNetIncome > 0 && budget > 0 ? Math.round(budget / derivedNetIncome * 100) : null;

  return <Card>
    <Sec icon="sliders" title={t('po.budgetVsSpending')}
      sub={t('po.budgetVsSpendingSub')}
      right={<Button size="sm" variant="secondary" icon="pencil" onClick={() => setEditOpen(true)} data-help={t('helpTopics.budgetPeriod.body')} data-help-title={t('helpTopics.budgetPeriod.title')}>{t('po.adjustBudget')}</Button>}
        help={t('po.budgetVsSpendingHelp')} />

    {editOpen && <PoBudgetEditor base={base} custom={budgets} onSave={(next) => { onSaveBudgets(next); setEditOpen(false); toast(t('pkn.for.budgetUpdated'), 'success'); }} onClose={() => setEditOpen(false)} />}
    {(derivedNetIncome > 0 || grossIncome > 0) && <div className="po-income-plan">
      <span><Icon name="arrowUpRight" size={15} />{t('pkn.for.plannedIncome')}</span>
      <b>{fmt(derivedNetIncome)}</b>
      <small>{grossIncome > 0 ? `${t('pkn.for.gross', { amount: fmt(grossIncome) })}${taxPercent > 0 ? ` · ${t('pkn.for.taxPct', { pct: taxPercent })}` : ''}` : t('pkn.for.netPerMonth')}{budgetShare !== null ? ` · ${t('pkn.for.budgetUses', { pct: budgetShare })}` : ''}</small>
    </div>}

    {withBudget.length === 0
      ? <div className="gj-empty" style={{ marginTop: 14 }}>{t('pkn.for.noBudgets')}</div>
      : <React.Fragment>
        <div className="po-bud-top">
          <div className="po-bud-total">
            <div className="po-bud-total-l">
              <span className="po-bud-total-spent">{fmt(spent)}</span>
              <span className="po-bud-total-of">{t('sharedText.transaction.by')} {fmt(budget)} {t('pkn.for.budgetedInPeriod')}</span>
            </div>
            <span className="po-bud-total-pct" style={{ color: tone.color }}>{totalPct} %</span>
          </div>
          <div className="po-bud-track lg"><div style={{ background: tone.color, width: `${Math.min(100, totalPct)}%` }} /></div>
          <div className="po-bud-total-foot">
            <span style={{ color: remaining < 0 ? 'var(--error)' : 'var(--muted)' }}>{remaining >= 0 ? t('pkn.for.leftInPeriod', { amount: fmt(remaining) }) : t('pkn.for.overBudget', { amount: fmt(-remaining) })}</span>
            <span>{overCount === 0 ? t('pkn.for.allWithin') : t(overCount === 1 ? 'pkn.for.overLimitOne' : 'pkn.for.overLimitMany', { count: overCount })}</span>
          </div>
        </div>

        <div className="po-bud-list">
          {withBudget.map((entry) => {
            const pct = entry.budget ? Math.round(entry.amount / entry.budget * 100) : 0;
            const rowTone = pofBudgetTone(pct);
            const diff = entry.budget - entry.amount;
            return <div key={entry.name}>
              <div className="po-bud-row-head">
                <span className="aii-cat-dot" style={{ background: entry.color, height: 12, width: 12 }} />
                <span className="po-bud-name">{entry.name}</span>
                <span className="po-bud-amt">{fmt(entry.amount)} <span className="muted">/ {fmt(entry.budget)}</span></span>
              </div>
              <div className="po-bud-track"><div style={{ background: rowTone.color, width: `${Math.min(100, pct)}%` }} /></div>
              <div className="po-bud-row-foot">
                <span className="po-bud-pct" style={{ color: rowTone.color }}><Icon name={rowTone.icon} size={13} />{t('pkn.for.pctUsed', { pct })}</span>
                <span className="po-bud-remain">{diff >= 0 ? t('pkn.for.diffLeft', { amount: fmt(diff) }) : t('pkn.for.diffOver', { amount: fmt(-diff) })}</span>
              </div>
            </div>;
          })}
        </div>
      </React.Fragment>}
  </Card>;
}

// ---------------- Forbruk ----------------
// Regelverket gir finkornede kategorier. Oversikten viser hovedkategori som
// standard, og detaljene ligger bak «Alle kategorier» eller utvidelse av raden.
function pofGroupRows(rows, customGroups = []) {
  const map = new Map();
  const customNames = new Set(customGroups);
  // En egen kategori som er merket som hovedkategori skal staa for seg selv,
  // ikke havne under «Annet» slik ukjente kategorier ellers gjoer.
  customNames.forEach((name) => map.set(name, { amount: 0, count: 0, custom: true, items: [], monthAmount: 0, name, prevMonthAmount: 0 }));
  rows.forEach((row) => {
    const name = customNames.has(row.name) ? row.name : (row.group || 'Annet');
    const current = map.get(name) || { amount: 0, count: 0, custom: false, items: [], monthAmount: 0, name, prevMonthAmount: 0 };
    current.amount += row.amount;
    current.count += row.count;
    current.monthAmount += row.monthAmount;
    current.prevMonthAmount += row.prevMonthAmount;
    current.items.push(row);
    map.set(name, current);
  });
  return [...map.values()]
    .sort((a, b) => b.amount - a.amount)
    .map((entry, index) => ({
      ...entry,
      budgetAmount: entry.amount,
      changePct: entry.prevMonthAmount ? ((entry.monthAmount - entry.prevMonthAmount) / Math.abs(entry.prevMonthAmount)) * 100 : 0,
      color: pofColor(index),
      items: entry.items.slice().sort((a, b) => b.amount - a.amount),
    }));
}

function PoForbruk({ availableMonths = [], budgets, categories = [], onOpenCategory, onPeriodChange, onRecurringDecision, onSaveBudgets, period, periodMonthCount = 1, savings = [], year }) {
  const Sec = window.PoSecHead;
  const [selected, setSelected] = pofUseState(null);
  const [detailed, setDetailed] = pofUseState(false);
  const [openGroup, setOpenGroup] = pofUseState('');
  const [periodOpen, setPeriodOpen] = pofUseState(false);

  const rows = pofUseMemo(() => categories.map((entry, index) => ({
    amount: Number(entry.amount || 0),
    budgetAmount: Number(entry.amount || 0),
    changePct: Number(entry.changePct || 0),
    color: entry.color || pofColor(index),
    count: entry.count || 0,
    group: entry.group || 'Annet',
    monthAmount: Number(entry.monthAmount || 0),
    name: entry.category || entry.name,
    prevMonthAmount: Number(entry.prevMonthAmount || 0),
  })), [categories]);
  const customGroups = pofUseMemo(() => (budgets?.custom || []).filter((entry) => entry.showAsGroup).map((entry) => entry.name), [budgets]);
  const groups = pofUseMemo(() => pofGroupRows(rows, customGroups), [rows, customGroups]);
  // Budsjettpanelet trenger kategorier uten forbruk i perioden, listen og
  // kakediagrammet skal ikke vise nullrader.
  const shown = (detailed ? rows : groups).filter((entry) => entry.amount > 0);
  const total = rows.reduce((sum, entry) => sum + entry.amount, 0);
  const biggest = shown[0];
  const periodKey = window.PoPeriods.isYearPeriod(period) ? '' : `${year}-${String(period).padStart(2, '0')}`;
  const periodSavings = periodKey ? savings.filter((item) => (item.months || []).includes(periodKey)) : savings;
  const switchMode = (next) => {
    setDetailed(next === 'alle');
    setSelected(null);
    setOpenGroup('');
  };
  // Indeksene i listen peker på andre kategorier etter et periodebytte.
  const switchPeriod = (next) => {
    onPeriodChange?.(next);
    setSelected(null);
    setOpenGroup('');
  };

  return <React.Fragment>
    <PoBudgetPanel categories={groups} budgets={budgets} onSaveBudgets={onSaveBudgets} periodMonthCount={periodMonthCount} />

    <Card>
      <Sec icon="pie" title={t('po.whereMoneyGoes')} sub={t('po.whereMoneyGoesSub')}
        help={t('po.whereMoneyGoesHelp')}
        right={<div className="row gap-10" style={{ flexWrap: 'wrap' }}>
          <button type="button" className="po-period-btn" onClick={() => setPeriodOpen(true)}
            title={t('po.selectPeriod')} aria-label={t('po.selectPeriod')} data-help={t('helpTopics.budgetPeriod.body')} data-help-title={t('helpTopics.budgetPeriod.title')}>{pofPeriodLabel(period)}<Icon name="chevDown" size={15} /></button>
          {shown.length > 0 && <Segmented value={detailed ? 'alle' : 'grupper'} onChange={switchMode} soft options={[
            { label: t('pkn.for.mainCategories'), value: 'grupper' },
            { label: t('pkn.for.allCategories'), value: 'alle' },
          ]} />}
        </div>} />
      {periodOpen && <PoPeriodPicker availableMonths={availableMonths} onClose={() => setPeriodOpen(false)} onSelect={switchPeriod} value={period} year={year} />}
      {shown.length === 0
        ? <div className="gj-empty" style={{ marginTop: 14 }}>{availableMonths.length > 0
          ? t('pkn.for.noSpendInPeriod', { period: pofPeriodLabel(period) })
          : t('pkn.for.noSpendData')}</div>
        : <React.Fragment>
          <div className="po-spend-grid" style={{ marginTop: 14 }}>
            <PoDonut data={shown} period={period} total={total} selected={selected} onSelect={setSelected} />
            <div className="po-spend-list" style={{ width: '100%' }}>
              {shown.map((entry, index) => {
                const expandable = !detailed && (entry.items || []).length > 1;
                const expanded = expandable && openGroup === entry.name;
                return <React.Fragment key={entry.name}>
                  <div data-app-control="true" className={`aii-cost-row${selected === index ? ' on' : ''}`}
                    style={{ cursor: 'pointer', gridTemplateColumns: '1fr 96px 78px 34px' }}
                    onClick={() => setSelected(selected === index ? null : index)} title={t('pkn.for.highlight', { name: entry.name })}>
                    <div className="aii-cost-name"><span className="aii-cat-dot" style={{ background: entry.color, height: 13, width: 13 }} />{entry.name}</div>
                    <div style={{ textAlign: 'right' }}>
                      <div className="aii-cost-amount">{fmt(entry.amount)}</div>
                      <div className="t-xs muted" style={{ fontWeight: 700 }}>{total ? Math.round(entry.amount / total * 100) : 0} %</div>
                    </div>
                    <span className="aii-cost-chg" style={{ color: entry.changePct > 0 ? 'var(--error)' : entry.changePct < 0 ? 'var(--success)' : 'var(--muted)' }}>
                      {entry.changePct !== 0 && <Icon name={entry.changePct > 0 ? 'trendUp' : 'trendDown'} size={13} />}
                      {entry.changePct > 0 ? '+' : ''}{Math.round(entry.changePct)} %
                    </span>
                    {expandable
                      ? <button className="aii-cost-more" onClick={(event) => { event.stopPropagation(); setOpenGroup(expanded ? '' : entry.name); }}
                        title={t(expanded ? 'pkn.for.hideCats' : 'pkn.for.showCats', { name: entry.name })} aria-label={t(expanded ? 'pkn.for.hideCats' : 'pkn.for.showCats', { name: entry.name })} aria-expanded={expanded}>
                        <Icon name={expanded ? 'chevDown' : 'chevRight'} size={18} />
                      </button>
                      : <button className="aii-cost-more" onClick={(event) => { event.stopPropagation(); onOpenCategory?.((entry.items || [])[0] || entry); }}
                        title={t('pkn.for.seeTx', { name: entry.name })} aria-label={t('pkn.for.seeTx', { name: entry.name })}><Icon name="chevRight" size={18} /></button>}
                  </div>
                  {expanded && (entry.items || []).map((item) => <div key={`${entry.name}-${item.name}`} className="aii-cost-row po-cost-sub"
                    style={{ gridTemplateColumns: '1fr 96px 78px 34px' }}>
                    <div className="aii-cost-name"><span className="aii-cat-dot" style={{ background: item.color, height: 9, width: 9 }} />{item.name}</div>
                    <div style={{ textAlign: 'right' }}>
                      <div className="aii-cost-amount">{fmt(item.amount)}</div>
                      <div className="t-xs muted" style={{ fontWeight: 700 }}>{entry.amount ? Math.round(item.amount / entry.amount * 100) : 0} {t('pkn.for.ofGroupSuffix')}</div>
                    </div>
                    <span className="aii-cost-chg" style={{ color: item.changePct > 0 ? 'var(--error)' : item.changePct < 0 ? 'var(--success)' : 'var(--muted)' }}>
                      {item.changePct !== 0 && <Icon name={item.changePct > 0 ? 'trendUp' : 'trendDown'} size={13} />}
                      {item.changePct > 0 ? '+' : ''}{Math.round(item.changePct)} %
                    </span>
                    <button className="aii-cost-more" onClick={() => onOpenCategory?.(item)}
                      title={t('pkn.for.seeTx', { name: item.name })} aria-label={t('pkn.for.seeTx', { name: item.name })}><Icon name="chevRight" size={18} /></button>
                  </div>)}
                </React.Fragment>;
              })}
            </div>
          </div>
          {biggest && <div className="aii-cost-flag">
            <Icon name="alertCircle" size={19} />
            <p>{t('pkn.for.biggest', { kind: detailed ? t('pkn.for.kindCost') : t('pkn.for.kindMain') })} <b>{biggest.name}</b> {t('pkn.for.biggestSuffix', { amount: fmt(biggest.amount), pct: total ? Math.round(biggest.amount / total * 100) : 0 })}</p>
          </div>}
        </React.Fragment>}
    </Card>

    <Card>
      <Sec icon="sparkle" title={t('po.recurringCharges')} sub={t('po.recurringChargesSub')} />
      {periodSavings.length === 0
        ? <div className="gj-empty" style={{ marginTop: 14 }}>{t('pkn.for.noRecurring')}</div>
        : <React.Fragment>
          <div className="aii-summary" style={{ marginBottom: 16, marginTop: 14 }}>
            <span className="aii-summary-ico"><Icon name="wallet" size={18} /></span>
            <p><b style={{ color: 'var(--ink)' }}>{t('pkn.for.recurringTotal', { amount: fmt(periodSavings.reduce((sum, item) => sum + Number(item.annualCost || 0), 0)) })}</b> {t('pkn.for.annualNote')}</p>
          </div>
          <div className="aii-reco-grid">
            {periodSavings.map((item) => <div key={item.id} className="aii-reco opp">
              <div className="aii-reco-top"><span className="aii-reco-badge"><Icon name={item.icon || 'sparkle'} size={13} />{t('po.opportunity')}</span></div>
              <div className="aii-reco-text">{item.text}</div>
              <div className="aii-reco-impact"><span className="lbl">{t('po.estimatedAnnualCost')}</span><span className="val">{fmt(item.annualCost)}</span></div>
              {item.confidence && <div className="t-xs muted" style={{ alignItems: 'center', display: 'flex', fontWeight: 700, gap: 6 }}><Icon name="sparkle" size={13} />{t('pkn.for.confidence', { pct: item.confidence })}</div>}
              <details className="today-card-details"><summary>{t('pkn.for.showTx')}</summary><div>{(item.observations || []).map((entry) => `${entry.date}: ${entry.description} (${fmt(entry.amount)})`).join(' · ')}</div></details>
              <div className="row gap-8" style={{ flexWrap: 'wrap' }}>
                <Button size="sm" variant="secondary" onClick={() => onRecurringDecision?.(item, 'confirmed')}>{t('pkn.for.yesSubscription')}</Button>
                <Button size="sm" variant="ghost" onClick={() => onRecurringDecision?.(item, 'not-subscription')}>{t('sharedText.clo.no')}</Button>
                <Button size="sm" variant="ghost" onClick={() => onRecurringDecision?.(item, 'active')}>{t('pkn.for.iUseIt')}</Button>
                <Button size="sm" variant="ghost" onClick={() => onRecurringDecision?.(item, 'cancel')}>{t('pkn.for.iWantToCancel')}</Button>
                <Button size="sm" variant="ghost" onClick={() => onRecurringDecision?.(item, 'snoozed')}>{t('pkn.for.remindLater')}</Button>
              </div>
            </div>)}
          </div>
        </React.Fragment>}
    </Card>
  </React.Fragment>;
}

Object.assign(window, { PoBudgetPanel, PoDonut, PoForbruk, PoPeriodPicker });
